import React, { useState } from 'react';

import { Trans, useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';
import { getExternalErrorMessage, logError } from '@modernloop/shared/utils';
import { Link, Stack, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import HelpCenterUrl from 'src/constants/HelpCenterUrl';

import useSmartRecruitersIntegrationPut from 'src/hooks/api/integration/useSmartRecruitersIntegrationPut';

import useCheckCreateCandidatePermission from './useCheckCreateCandidatePermission';

type Props = {
  jobId: string;
  onHasPermission: () => void;
  onClose: () => void;
};

const SmartRecruitersCreateCandidatePermissionDialog = ({ jobId, onHasPermission, onClose }: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [apiKeyVal, setApiKeyVal] = useState('');

  const putSmartRecruiters = useSmartRecruitersIntegrationPut();

  const { enqueueSnackbar } = useSnackbar();
  const checkCreateCandidatePermission = useCheckCreateCandidatePermission(jobId);

  const handleCheckPermission = async () => {
    try {
      const { hasPermission } = await checkCreateCandidatePermission();
      if (hasPermission) {
        onHasPermission();
      }
    } catch (error) {
      enqueueSnackbar(getExternalErrorMessage(error), { variant: 'error' });
      logError(error, { jobId });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open
      maxWidth="xs"
      onClose={onClose}
      title={t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.smartRecruiters.title')}
      decoration={
        <img
          style={{ width: '64px', height: '64px' }}
          src="/static/images/scheduling/warning_64.png"
          alt={t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.changingCalendarWarningAlt')}
        />
      }
      cancelOptions={{
        label: t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.cancel'),
        onClick: onClose,
      }}
      submitOptions={{
        label: t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.continue'),
        isLoading: loading,
        isDisabled: !apiKeyVal,
        onClick: () => {
          setLoading(true);
          putSmartRecruiters.mutate(
            {
              apiKey: apiKeyVal,
              active: true,
            },
            {
              onSuccess: handleCheckPermission,
              onError: (e) => {
                enqueueSnackbar(getExternalErrorMessage(e), { variant: 'error' });
              },
            }
          );
        },
      }}
    >
      <Stack spacing={1}>
        <Stack direction="column">
          <Typography variant="subtitle1">
            {t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.smartRecruiters.apiKey')}
          </Typography>
          <Typography color="text.secondary">
            <Trans
              i18nKey="hiring-dashboard-jobs:jobs.sourcingLinks.permissions.smartRecruiters.apiKeyDescription"
              components={{
                credentialsLink: (
                  <Link
                    target="_blank"
                    href="https://www.smartrecruiters.com/settings/administration/app-management/custom-applications/credentials/create"
                  />
                ),
              }}
            />
          </Typography>
        </Stack>

        <TextField
          value={apiKeyVal}
          placeholder={t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.smartRecruiters.enterApiKey')}
          onChange={(e) => setApiKeyVal(e.target.value)}
          fullWidth
        />
        <Typography color="text.secondary" variant="body2">
          <Trans
            i18nKey="hiring-dashboard-jobs:jobs.sourcingLinks.permissions.smartRecruiters.needHelp"
            components={{
              learnMoreLink: <Link target="_blank" href={HelpCenterUrl.SMART_RECRUITERS_INTEGRATION} />,
            }}
          />
        </Typography>
      </Stack>
    </Dialog>
  );
};

export default SmartRecruitersCreateCandidatePermissionDialog;
